import React, { useState } from 'react';
import { NavLink, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Home, FileText, BarChart3, Users, Activity,
  LogOut, Menu, X, HelpCircle, ArrowLeftRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AmigosWordmark from '@/components/AmigosWordmark';
import Overview from './Overview';
import LeadInbox from './LeadInbox';
import LeadDetail from './LeadDetail';
import Reports from './Reports';
import UsersView from './Users';
import SettingsView from './Settings';
import AuditLog from './AuditLog';

const DashboardApp = () => {
  const { user, token, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading...</div>;
  if (!user) return <Navigate to="/dashboard/login" replace />;

  const isManager = user.role === 'manager';
  const canSwitchBrand = (user.permissions || []).includes('cross_brand:dashboard_switch');

  /* ── Nav items mirror Lone Star exactly: HOME / SUBMISSIONS /
     (manager) ANALYTICS / USERS / AUDIT LOG. Same wording, same order. */
  const navItems = [
    { path: '/dashboard/overview', icon: <Home size={22} />, label: 'HOME',
      hint: 'Your main screen — greeting, new alerts, quick overview' },
    { path: '/dashboard/leads', icon: <FileText size={22} />, label: 'SUBMISSIONS',
      hint: 'View, search, and print all forms people submitted', badge: true },
    ...(isManager ? [
      { path: '/dashboard/reports', icon: <BarChart3 size={22} />, label: 'ANALYTICS',
        hint: 'Your business report — charges, areas, demographics' },
      { path: '/dashboard/users', icon: <Users size={22} />, label: 'USERS',
        hint: 'Add or remove people who can access this dashboard' },
      { path: '/dashboard/audit', icon: <Activity size={22} />, label: 'AUDIT LOG',
        hint: 'See every action taken in this system (who did what, when)' },
    ] : []),
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const doLogout = () => { logout(); navigate('/dashboard/login'); };

  const openLoneStar = async () => {
    setSwitching(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await axios.post(`${API}/sso/switch-request`, { target_brand: 'lonestar' },
        { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.redirect_url;
    } catch {
      setSwitching(false);
    }
  };

  return (
    <div className="amigos-app dashboard-layout" data-testid="dashboard-layout">
      {/* ── SIDEBAR — mirrors Lone Star structure exactly ── */}
      <aside className={`dash-sidebar ${sidebarOpen ? 'dash-sidebar--open' : ''}`} data-testid="dash-sidebar">
        <div className="dash-sidebar__header" style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <AmigosWordmark size="sm" subline={null} testId="sidebar-wordmark" />
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-alt)', letterSpacing: '0.14em', marginTop: '0.65rem' }}>LICENSE #0111 · CONROE, TX</div>
          <div style={{ marginTop: '0.75rem' }}>
            <span className={`dash-sidebar__role dash-sidebar__role--${user.role}`}>
              {user.role === 'manager' ? 'MANAGER' : user.role.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }} data-testid="dash-user-name">
            {user.name || user.email}
          </div>
        </div>

        <nav className="dash-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={`dash-nav__item ${isActive(item.path) ? 'dash-nav__item--active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              title={item.hint}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.icon}
              <div style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{item.label}</span>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', lineHeight: 1.3, marginTop: '0.15rem', opacity: 0.8, fontFamily: 'var(--font-body)', letterSpacing: 0, textTransform: 'none' }}>
                  {item.hint}
                </div>
              </div>
              {item.badge && <span className="dash-nav__badge" data-testid="new-submissions-badge">!</span>}
            </NavLink>
          ))}
        </nav>

        {/* Cross-brand SSO switch — Amigos-specific, since Amigos jumps to Lone Star */}
        {canSwitchBrand && (
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-dim)' }}>
            <button
              onClick={openLoneStar}
              disabled={switching}
              className="amg-btn-outline"
              style={{ width: '100%', justifyContent: 'center' }}
              data-testid="open-lonestar-dashboard-btn"
              title="Switch to the Lone Star Bonding dashboard — no re-login required"
            >
              <ArrowLeftRight size={15} /> {switching ? 'Redirecting…' : 'Open Lone Star Dashboard'}
            </button>
          </div>
        )}

        {/* Need Help section — same wording as Lone Star */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-dim)', background: 'rgba(177, 133, 74, 0.02)' }} data-testid="need-help-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <HelpCircle size={14} style={{ color: 'var(--gold)' }} />
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-alt)' }}>Need help?</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Call James at <strong style={{ color: 'var(--gold)' }}>Texas IT Nerds</strong> for any tech questions.
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-dim)' }}>
          <button onClick={doLogout} className="amg-btn-outline" style={{ width: '100%', justifyContent: 'center', gap: '0.75rem' }} data-testid="dash-logout-btn">
            <LogOut size={18} /> LOGOUT
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT — mirrors Lone Star (mobile header only, no top strip) ── */}
      <main className="dash-content">
        <div className="dash-mobile-header" data-testid="dash-mobile-header">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="dash-mobile-toggle" data-testid="dash-mobile-toggle" aria-label="Toggle sidebar">
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <span className="dash-mobile-title">AMIGO&apos;S BAIL BONDS</span>
        </div>

        <Routes>
          <Route path="overview" element={<Overview />} />
          <Route path="leads" element={<LeadInbox />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          {isManager && <Route path="reports" element={<Reports />} />}
          {isManager && <Route path="users" element={<UsersView />} />}
          {isManager && <Route path="audit" element={<AuditLog />} />}
          {/* Settings route accessible via direct URL for future admin needs, hidden from nav to match Lone Star */}
          {isManager && <Route path="settings" element={<SettingsView />} />}
          <Route path="*" element={<Navigate to="overview" replace />} />
        </Routes>
      </main>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', zIndex: 999 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardApp;
